import React from 'react';
import PropTypes from 'prop-types';

import validDragEvents from './validDragEvents';
import GapChoice from './GapChoice';
import LoadTracker from '../../../../services/LoadTracker';
import styles from './gapMatchInteraction.scss';
import settingsReader from '../../../../store/readHelpers/SettingsReader';
import HiddenItemFlagButton from '../../itemFlag/HiddenItemFlagButton';
import scorableItemNumberingService from '../../../../services/questionView/ScorableItemNumberingService';

class Gap extends React.Component {
    static propTypes = {
        gapModel: PropTypes.object.isRequired,
        gapChoiceModels: PropTypes.array,
        gapWidth: PropTypes.number.isRequired,
        gapHeight: PropTypes.number.isRequired,
        handleUserResponse: PropTypes.func.isRequired,
        reportReady: PropTypes.func.isRequired,
        orderNumber: PropTypes.string,
        services: PropTypes.object,
        questionModel: PropTypes.object,
        model: PropTypes.object,
        isActive: PropTypes.bool,
        isFlagged: PropTypes.bool,
        extendedRenderingActivated: PropTypes.bool,
    };

    constructor(props) {
        super(props);
        this.state = { dragOver: false };
        this.readyTracker = new LoadTracker(props.reportReady, `Gap ${props.gapModel.identifier}`);
        this.gapChoiceTrackers = props.gapChoiceModels
            .reduce((trackers, gapChoiceModel) => {
                trackers[gapChoiceModel.identifier] = this.readyTracker.addItemToTrack(`GapChoice ${gapChoiceModel.identifier}`);
                return trackers;
            }, {});
        this.readyTracker.setAllItemsAdded();
        this.navigateToScorableItem = this.navigateToScorableItem.bind(this);
    }

    getDraggedGapTextIdentifier = (event) => {
        try {
            return JSON.parse(event.dataTransfer.getData('text'));
        } catch (error) {
            return {};
        }
    };

    isValidDragEvent = (event) => {
        return validDragEvents.indexOf(event) !== -1;
    };

    onDragEnter = (event) => {
        if (this.isValidDragEvent(event)) {
            event.preventDefault();
            this.setState({ dragOver: true });
        }
    };

    onDragOver = (event) => {
        if (this.isValidDragEvent(event)) {
            event.preventDefault();
        }
    };

    onDragLeave = (event) => {
        event.preventDefault();
        this.setState({ dragOver: false });
    };

    onDrop = (event) => {
        const { gapIdentifier, gapTextIdentifier, questionId } = this.getDraggedGapTextIdentifier(event);
        if (gapTextIdentifier) {
            delete validDragEvents[event.timestamp];
            event.preventDefault();
            const { gapModel, handleUserResponse, questionModel } = this.props;

            if (questionModel.contentRevisionId === questionId) {
                handleUserResponse(gapTextIdentifier, gapModel.identifier, gapIdentifier);
                this.navigateToScorableItem();
            }

            this.setState({ dragOver: false });
            document.querySelectorAll(`[data-identifier=${gapTextIdentifier}]`)[0].classList.remove('dragging');
        }
    };

    navigateToScorableItem() {
        const { questionModel, model, gapModel } = this.props;
        scorableItemNumberingService.navigateToScorableItem(questionModel.contentRevisionId, model.identifier, gapModel.identifier, true);
    }

    doubleClickHandler = (event) => {
        const { gapModel, handleUserResponse } = this.props;
        const gapTextIdentifier = event.target.dataset.identifier;
        handleUserResponse(gapTextIdentifier, undefined, gapModel.identifier);
    };

    gerOrderNumber() {
        const { gapChoiceModels, orderNumber } = this.props;
        if (!gapChoiceModels.length) {
            return (
                <span className="gapOrderNumber">{orderNumber}</span>
            );
        }
        return null;
    }

    render() {
        const {
            questionModel,
            gapModel,
            gapChoiceModels,
            gapWidth,
            gapHeight,
            isActive,
            isFlagged,
            orderNumber,
            extendedRenderingActivated,
            model,
        } = this.props;
        const isPageBreakingPrintView = settingsReader.shouldPageBreakOnPrintView();
        const style = extendedRenderingActivated ? undefined : { width: `${gapWidth + 3}px`, height: `${gapHeight + 2}px` };
        const addHiddenFocusableFlag = scorableItemNumberingService
            .shouldAddHiddenFlag(questionModel.contentRevisionId, model.identifier, gapModel.identifier);

        const classList = [styles.dropZone, 'gap'];
        if (isActive) {
            classList.push('active');
        }
        if (isFlagged) {
            classList.push('flagged');
        }
        if (gapChoiceModels.length) {
            classList.push('populated');
        }
        if (this.state.dragOver) {
            classList.push('drag-over');
        }
        if (extendedRenderingActivated) {
            classList.push('extendedGap');
        }
        if (extendedRenderingActivated && gapChoiceModels.length) {
            classList.push('extendTargetGapWidth');
        }
        if (isFlagged) {
            classList.push('flagged');
        }
        if (isPageBreakingPrintView) {
            classList.push(styles.breakingPageView);
        }

        return (
            <React.Fragment>
                <div
                    className={classList.join(' ')}
                    data-type="drop"
                    data-identifier={gapModel.identifier}
                    onDragEnter={this.onDragEnter}
                    onDragOver={this.onDragOver}
                    onDragLeave={this.onDragLeave}
                    onDrop={this.onDrop}
                    onDoubleClick={this.doubleClickHandler}
                    style={style}
                    tabIndex={0}
                    onFocus={this.navigateToScorableItem}
                    role="button"
                    id={orderNumber ? `scorableItem-${questionModel.contentRevisionId}_${orderNumber}` : undefined}
                >
                    {this.gerOrderNumber()}
                    {
                        gapChoiceModels.map((gapChoiceModel) => {
                            return (
                                <GapChoice
                                    {...this.props}
                                    gapChoiceModel={gapChoiceModel}
                                    gapModel={gapModel}
                                    key={gapChoiceModel.identifier}
                                    reportReady={this.gapChoiceTrackers[gapChoiceModel.identifier]}
                                    shouldAutoSizeTokens
                                />
                            );
                        })
                    }
                </div>
                {
                    addHiddenFocusableFlag && <HiddenItemFlagButton
                        orderNumber={orderNumber}
                        responseIdentifier={gapModel.identifier}
                        contentRevisionId={questionModel.contentRevisionId}
                        choiceIdentifier={gapModel.identifier}
                    />
                }
            </React.Fragment>
        );
    }
}

export default Gap;
